import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Select,
  MenuItem,
  Typography,
  InputLabel,
  FormControl,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import RoleCreation from "../role_creation";
import roleApi from "../../../services/role";
import userApi from "../../../services/user";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../../redux/loader/actions";
import { showSnackbar } from "../../../redux/snackbar/actions";
import { encryptAES, decryptAES } from "../../../utils";

const useStyles = makeStyles({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },
  },
  dialogTitleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    marginLeft: "auto",
  },
});

const UserFormModal = ({
  open,
  onClose,
  onSave,
  existingUser,
  createdRole,
  mode,
  userEdit,
  getAllUser,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [roleList, setRoleList] = useState([]);
  const [customRoleModalOpen, setCustomRoleModalOpen] = useState(false);
  const [selectedRoleUuid, setSelectedRoleUuid] = useState("");
  const handleSubmit = (formData) => {
    const userid = userEdit?.user?.uuid;
    const roleid = selectedRoleUuid?.uuid;
    const data = {
      role_uuid: selectedRoleUuid?.uuid,
      username: formData.name,
      email: formData?.email?.toLowerCase(),
      emp_id: formData.emp_id,
      mobile_number: formData.mobilenumber,
    };
    const encryptedData = encryptAES(data);
    dispatch(showLoader());
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Info updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        onClose();
        roleSelect();
        getAllUser();
        // window.location.reload();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Internal error",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    if (mode === "edit") {
      userApi
        .updateUser(userid, roleid, encryptedData)
        .then(onSuccess, onFailure);
    } else {
      userApi.createUser(encryptedData).then(onSuccess, onFailure);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobilenumber: "",
      emp_id: "",
      role: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .min(3, "Name should be at least 3 characters"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      mobilenumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
        .required("Mobile number is required"),
      emp_id: Yup.string().required("Employee ID is required"),
      role: Yup.string().required("Role is required"),
    }),
    onSubmit: handleSubmit,
  });

  const roleSelect = useCallback(() => {
    dispatch(showLoader());
    roleApi
      .getAllRole()
      .then((res) => {
        dispatch(hideLoader());
        if (res?.data?.status) {
          setRoleList(res.data.data);
        }
      })
      .catch((err) => {
        dispatch(hideLoader());
        console.error("Error fetching roles:", err);
      });
  }, [dispatch]);

  useEffect(() => {
    if (open) {
      roleSelect();
    }
  }, [roleSelect, open]);
  useEffect(() => {
    if (open && mode === "edit" && userEdit) {
      formik.setValues({
        name: userEdit.user?.username || "",
        email: userEdit.user?.email || "",
        mobilenumber: userEdit.user?.mobile_number || "",
        emp_id: userEdit.user?.emp_id || "",
        role: userEdit.role?.role || "",
      });
      setSelectedRoleUuid(userEdit.role || "");
    }
  }, [open, mode, userEdit]);

  const handleCustomRoleSave = (roleName) => {
    formik.setFieldValue("role", selectedRoleUuid?.role);

    setCustomRoleModalOpen(false);
  };
  useEffect(() => {
    if (mode === "edit") {
      formik.setFieldValue("role", selectedRoleUuid?.role);
    }
  }, [selectedRoleUuid]);
  const handleClose = () => {
    formik.resetForm();
    onClose();
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle disableTypography className={classes.dialogTitleContainer}>
        {mode === "edit" ? "Edit User" : "Add User"}
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="name"
              label="Name"
              variant="outlined"
              fullWidth
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="email"
              label="Official Email"
              variant="outlined"
              fullWidth
              disabled={mode === "edit"}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="mobilenumber"
              label="Mobile Number"
              variant="outlined"
              disabled={mode === "edit"}
              fullWidth
              value={formik.values.mobilenumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.mobilenumber &&
                Boolean(formik.errors.mobilenumber)
              }
              helperText={
                formik.touched.mobilenumber && formik.errors.mobilenumber
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              name="emp_id"
              label="Employee ID"
              variant="outlined"
              fullWidth
              value={formik.values.emp_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.emp_id && Boolean(formik.errors.emp_id)}
              helperText={formik.touched.emp_id && formik.errors.emp_id}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <FormControl
              fullWidth
              variant="outlined"
              error={Boolean(formik.errors.role && formik.touched.role)}
            >
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                name="role"
                labelId="role-label"
                label="Role"
                value={formik.values.role}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  const roleItem = roleList.find(
                    (item) => item.role === selectedValue
                  );

                  if (selectedValue === "custom") {
                    setCustomRoleModalOpen(true);
                  } else {
                    formik.setFieldValue("role", selectedValue);
                    if (roleItem) {
                      setSelectedRoleUuid(roleItem);
                    }
                  }
                }}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="" disabled>
                  Select Role
                </MenuItem>
                <MenuItem value="custom">Create Custom Role</MenuItem>
                {roleList.map((roleItem) => (
                  <MenuItem key={roleItem.id} value={roleItem.role}>
                    {roleItem.role.charAt(0).toUpperCase() +
                      roleItem.role.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.role && formik.errors.role && formik.dirty && (
                <Typography color="error" variant="caption">
                  {formik.errors.role}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box m={2}>
          <Button
            variant="contained"
            color="primary"
            className={classes.animatedButton}
            onClick={formik.handleSubmit}
          >
            {mode === "edit" ? "Update" : "Add User"}
          </Button>
        </Box>
      </DialogActions>
      <RoleCreation
        open={customRoleModalOpen}
        onClose={() => setCustomRoleModalOpen(false)}
        onSave={handleCustomRoleSave}
        roleSelect={roleSelect}
        setSelectedRoleUuid={setSelectedRoleUuid}
      />
    </Dialog>
  );
};

export default UserFormModal;
